import { RequestBody, RequestParams } from '~/core/api/RequestTypes';
import { ResponseBody, ResponseData } from '~/core/api/ResponseTypes';
import SRError, { isSRError } from '~/utils/errors/srError';

export interface ResultsRequestInputs {
    apiUrl: string;
    searchQuery: string;
    parameters: RequestParams;
    /** Used for aborting results request */
    abortController: AbortController;
    isDebugMode: boolean;
}

/**
 * Used for fetching search results from the provided api.
 *
 * @returns a promise that resolves to the response data
 */
export const resultsRequest = async ({
    apiUrl,
    searchQuery,
    parameters,
    abortController,
    isDebugMode,
}: ResultsRequestInputs): Promise<ResponseData> => {
    const requestBody: RequestBody = {
        query: searchQuery,
        sets: parameters,
    };

    if (isDebugMode) {
        console.log('SERP-REQUEST', requestBody);
    }

    try {
        const headers = new Headers();
        headers.set('Content-Type', 'application/json');

        if (isDebugMode) {
            abortController.signal.addEventListener('abort', () => {
                console.log('Request aborted', searchQuery);
            });
        }

        const response = await fetch(apiUrl, {
            signal: abortController.signal,
            method: 'POST',
            headers: headers,
            body: JSON.stringify(requestBody),
        });

        // TODO: consder runtime type checking
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const { data, errors }: ResponseBody = await response.json();

        if (response.ok && data !== undefined) {
            if (isDebugMode) {
                console.log('SERP-RESPONSE', data, { parameters, searchQuery });
            }
            return data;
        }

        const error = new SRError(errors?.map((e) => e.message).join('\n') ?? 'unknown');
        return Promise.reject(error);
    } catch (e) {
        return Promise.reject(e);
    }
};

export interface SuccessCallbackParams {
    data: ResponseData;
    searchQuery: string;
    requestParams: RequestParams;
}

export interface FetchResultsParams {
    apiUrl: string;
    searchQuery: string;
    parameters: RequestParams;
    successCallback: (params: SuccessCallbackParams) => void;
    failedCallback: (error: SRError) => void;
    abortController: AbortController;
    isDebugMode: boolean;
}

const fetchResults = async ({
    apiUrl,
    searchQuery,
    parameters,
    successCallback,
    failedCallback,
    abortController,
    isDebugMode,
}: FetchResultsParams) => {
    try {
        const data = await resultsRequest({ apiUrl, searchQuery, parameters, abortController, isDebugMode });
        successCallback({ data, searchQuery, requestParams: parameters });
    } catch (error) {
        failedCallback(isSRError(error) ? error : new SRError('Result fetch failed', error));
    }
};

export default fetchResults;
